import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip, Chip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";

const stringToColor = (string) => {
	let hash = 0;
	let i;
	/* eslint-disable no-bitwise */
	for (i = 0; i < string.length; i += 1) {
		hash = string.charCodeAt(i) + ((hash << 5) - hash);
	}
	let color = '#';
	for (i = 0; i < 3; i += 1) {
		const value = (hash >> (i * 8)) & 0xff;
		color += `00${value.toString(16)}`.substr(-2);
	}
	/* eslint-enable no-bitwise */
	return color;
};
import toastError from "../../errors/toastError";
import TicketPeekModal from "../TicketPeekModal";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
	},

	pendingTicket: {
		cursor: "unset",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
	},

	lastMessageTime: {
		justifySelf: "flex-end",
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		paddingRight: 20,
	},

	newMessagesCount: {
		alignSelf: "center",
		marginRight: 8,
		marginLeft: "auto",
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	acceptButton: {
		position: "absolute",
		left: "50%",
	},

	ticketQueueColor: {
		flex: "none",
		width: "8px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
	},

	userTag: {
		position: "absolute",
		marginRight: 5,
		right: 5,
		bottom: 5,
		background: "#2576D2",
		color: "#ffffff",
		border: "1px solid #CCC",
		padding: 1,
		paddingLeft: 5,
		paddingRight: 5,
		borderRadius: 10,
		fontSize: "0.9em"
	},
}));

const TicketListItem = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handlePeekTicket = () => {
		setOpenTicketMessageDialog(true);
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	return (
		<div
			key={ticket.id}
			className={clsx("p-3 border-bottom d-flex align-items-center position-relative cursor-pointer", {
				"bg-light": ticketId && +ticketId === ticket.id,
				"opacity-75": ticket.status === "pending"
			})}
			onClick={e => {
				if (ticket.status === "pending") return;
				handleSelectTicket(ticket.id);
			}}
			style={{ transition: 'background-color 0.2s' }}
		>
			<div className="position-relative">
				{ticket?.contact?.profilePicUrl ? (
					<img
						src={ticket.contact.profilePicUrl}
						className="rounded-circle avatar-md"
						width="45"
						height="45"
						style={{ objectFit: 'cover' }}
						alt={ticket.contact.name}
					/>
				) : (
					<div className="rounded-circle avatar-md avatar-letter">
						{ticket.contact.name?.charAt(0).toUpperCase() || "?"}
					</div>
				)}
				<div
					className="position-absolute bottom-0 end-0 border border-white border-2 rounded-circle"
					style={{
						width: '12px',
						height: '12px',
						backgroundColor: ticket.unreadMessages > 0 ? '#10B981' : (ticket.queue?.color || "#7C7C7C"),
						transition: 'background-color 0.3s ease'
					}}
					title={ticket.unreadMessages > 0 ? "Nova mensagem" : (ticket.queue?.name || "Sem fila")}
				></div>
			</div>

			<div className="ms-3 flex-grow-1 overflow-hidden">
				<div className="d-flex align-items-center justify-content-between mb-1">
					<h6 className="mb-0 text-truncate text-dark fw-bold" style={{ fontSize: '0.95rem' }}>
						{ticket.contact.name}
					</h6>
					<small className="text-muted ms-2 flex-shrink-0">
						{ticket.lastMessage && (
							isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
								format(parseISO(ticket.updatedAt), "HH:mm")
							) : (
								format(parseISO(ticket.updatedAt), "dd/MM/yyyy")
							)
						)}
					</small>
				</div>

				<div className="d-flex align-items-center justify-content-between">
					<div className="d-flex align-items-center w-100 text-truncate">
						{ticket.whatsapp?.name && (
							<Chip
								label={ticket.whatsapp.name}
								size="small"
								style={{
									backgroundColor: stringToColor(ticket.whatsapp.name),
									color: "#FFF",
									marginRight: 8,
									height: 18,
									fontSize: "0.7rem",
									fontWeight: "bold",
									cursor: "pointer",
									flexShrink: 0
								}}
								title={`Conexão: ${ticket.whatsapp.name}`}
							/>
						)}
						<span className="text-muted text-truncate" style={{ fontSize: '0.85rem' }}>
							{ticket.lastMessage ? (
								<MarkdownWrapper>
									{(() => {
										try {
											if (ticket.lastMessage && ticket.lastMessage.trim().startsWith("{")) {
												const jsonBody = JSON.parse(ticket.lastMessage);
												if (jsonBody.pollCreationMessage || jsonBody.type === "poll") {
													return "📊 Enquete";
												}
												if (jsonBody.pollCreationMessageKey && jsonBody.vote) {
													return "🗳️ Voto Recebido";
												}
												if (jsonBody.interactiveMessage || jsonBody.InteractiveMessage) {
													return "🛍️ Pedido/Pagamento";
												}
												return jsonBody.title || jsonBody.singleSelectReply?.selectedRowID || ticket.lastMessage;
											}
											return ticket.lastMessage;
										} catch (e) {
											return ticket.lastMessage;
										}
									})()}
								</MarkdownWrapper>
							) : (
								<span>Nova conversa</span>
							)}
						</span>
					</div>

					{ticket.unreadMessages > 0 && (
						<span className="badge bg-success rounded-pill ms-2">
							{ticket.unreadMessages}
						</span>
					)}
				</div>
			</div>

			{
				ticket.status === "pending" && (
					<div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 5 }}>
						<button
							className="btn btn-success btn-sm me-2 shadow-sm"
							onClick={e => {
								e.stopPropagation();
								handleAcepptTicket(ticket.id);
							}}
						>
							Aceitar
						</button>
						<button
							className="btn btn-white btn-sm shadow-sm"
							onClick={e => {
								e.stopPropagation();
								handlePeekTicket(ticket.id);
							}}
						>
							Espiar
						</button>
					</div>
				)
			}

			<TicketPeekModal
				open={openTicketMessageDialog}
				onClose={() => setOpenTicketMessageDialog(false)}
				ticketId={ticket.id}
			/>
		</div >
	);
};

export default TicketListItem;
